if (!customElements.get('theme-drawer')) {
  const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

  class ThemeDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = null;
      this.panel = null;
      this.handle = null;
      this.dragSurface = null;
      this.previousActiveElement = null;
      this.focusableElements = null;
      this._dragController = null;
      this._dragAbortController = null;
      this._desktopQuery = null;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this._syncDragListeners = this._syncDragListeners.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
      this.classList.add('is-hydrated');

      if (this.dragToClose) {
        this.handle = this.querySelector('[data-drawer-handle]');
        this.dragSurface = this.handle ? this.panel : null;
        if (this.sideDesktop) {
          this._desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
          this._desktopQuery.addEventListener('change', this._syncDragListeners);
        }
        this._syncDragListeners();
      }
    }

    disconnectedCallback() {
      this._removeListeners();
      this._desktopQuery?.removeEventListener('change', this._syncDragListeners);
      this._dragAbortController?.abort();
      this._dragController?.reset();
    }

    get side() {
      return this.dataset.side || 'right';
    }

    get sideDesktop() {
      return this.dataset.sideDesktop || null;
    }

    get trapFocus() {
      return this.dataset.trapFocus !== 'false';
    }

    get closeOnEsc() {
      return this.dataset.closeOnEsc !== 'false';
    }

    get closeOnOverlayClick() {
      return this.dataset.closeOnOverlayClick !== 'false';
    }

    get dragToClose() {
      return this.dataset.dragToClose === 'true';
    }

    open() {
      if (this.hasAttribute('data-open')) return;

      this.previousActiveElement = document.activeElement;
      this.setAttribute('data-open', '');
      this._updateFocusableElements();
      this._addListeners();

      requestAnimationFrame(() => {
        this.focusableElements?.[0]?.focus({ preventScroll: true });
      });

      this._publish(theme?.pubsub?.events?.DRAWER_OPEN);
    }

    close() {
      this.removeAttribute('data-open');
      this._removeListeners();

      this.previousActiveElement?.focus();
      this.previousActiveElement = null;

      this._publish(theme?.pubsub?.events?.DRAWER_CLOSE);
    }

    toggle() {
      this.hasAttribute('data-open') ? this.close() : this.open();
    }

    _addListeners() {
      document.addEventListener('keydown', this.handleKeyDown);
      if (this.closeOnOverlayClick) {
        this.overlay?.addEventListener('click', this.handleOverlayClick);
      }
    }

    _removeListeners() {
      document.removeEventListener('keydown', this.handleKeyDown);
      this.overlay?.removeEventListener('click', this.handleOverlayClick);
    }

    handleKeyDown(e) {
      if (e.key === 'Escape' && this.closeOnEsc) {
        this.close();
        return;
      }

      if (e.key === 'Tab' && this.trapFocus) {
        this._handleTabKey(e);
      }
    }

    handleOverlayClick() {
      this.close();
    }

    _handleTabKey(e) {
      const first = this.focusableElements?.[0];
      const last = this.focusableElements?.[this.focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first?.focus();
        e.preventDefault();
      }
    }

    refreshFocus() {
      this._updateFocusableElements();
    }

    _updateFocusableElements() {
      const selector = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this.focusableElements = [...(this.panel ?? this).querySelectorAll(selector)].filter(
        el => !el.hidden && !el.disabled && el.offsetParent !== null
      );
    }

    _effectiveSide() {
      if (this.sideDesktop && this._desktopQuery?.matches) {
        return this.sideDesktop;
      }
      return this.side;
    }

    _syncDragListeners() {
      const shouldDrag = this.dragToClose && this._effectiveSide() === 'bottom' && this.dragSurface;

      if (shouldDrag && !this._dragAbortController) {
        this._dragController = theme.utils.createDragToClose({
          host: this,
          panel: this.panel,
          scrollRegionSelector: '.drawer__body',
          onClose: () => this.close(),
        });

        this._dragAbortController = new AbortController();
        const signal = this._dragAbortController.signal;
        this.dragSurface.addEventListener('pointerdown', this._dragController.onDragStart, { signal });
        document.addEventListener('pointermove', this._dragController.onDragMove, { signal });
        document.addEventListener('pointerup', this._dragController.onDragEnd, { signal });
        document.addEventListener('pointercancel', this._dragController.onDragEnd, { signal });
      } else if (!shouldDrag && this._dragAbortController) {
        this._dragAbortController.abort();
        this._dragAbortController = null;
        this._dragController?.reset();
        this._dragController = null;
        this.panel?.style.removeProperty('--drawer-drag-offset');
      }
    }

    _publish(eventName) {
      if (!eventName || typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(eventName, { id: this.id });
    }
  }

  customElements.define('theme-drawer', ThemeDrawer);
}
